import {
  faClipboardCheck,
  faCode,
  faDatabase,
  faFileLines,
  faFolderOpen,
  faKey,
  faShapes,
  faSliders,
  faStore,
  faUserSecret,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';
import type {
  NavChild,
  NavEntry,
  NavGroup,
} from '@/components/shared/Sidebar/types';
import {
  MANAGEMENT_DEFAULT_ROUTE,
  MANAGEMENT_ROOT,
} from '@/lib/utils/managementAccess';

/** What the nav needs to know about who is looking at it. */
export interface ManagementNavAccess {
  /** `RETRACED_ADMIN` specifically, not any internal role. */
  isRetracedAdmin: boolean;
  /** Whether this staff member may open the Impersonate tool — see `canImpersonate`. */
  canImpersonate: boolean;
  /** Whether the app has a route for an href — see `onlyBuilt`. */
  hasRoute: (href: string) => boolean;
}

/** One entry. Every href is an in-app route under `/management`. */
function entry(key: string, label: string, slug: string): NavChild {
  return { key, label, href: `${MANAGEMENT_ROOT}/${slug}` };
}

/**
 * Drops every entry the app has no route for, and any group left empty by
 * that.
 *
 * The full menu below is the migration's target shape, so entries are written
 * once and appear as their page lands rather than being added one at a time.
 * Listing them all from the start was tried first: an unmigrated entry opened
 * Not Found, which reads as a broken page rather than as work still to come.
 *
 * The route table is the test — an href with no route is exactly what would
 * 404 — so the menu cannot drift from what is actually built.
 */
function onlyBuilt(
  entries: NavEntry[],
  hasRoute: (href: string) => boolean,
): NavEntry[] {
  const built: NavEntry[] = [];
  for (const item of entries) {
    if (item.type === 'section') {
      built.push(item);
      continue;
    }
    if (item.type === 'item') {
      if (!item.href || hasRoute(item.href)) built.push(item);
      continue;
    }
    const children = item.children.filter(c => hasRoute(c.href));
    if (children.length > 0) built.push({ ...item, children });
  }
  return built;
}

/**
 * Rail entries for the management area — the same `NavEntry[]` shape `buildNav`
 * returns, so the sidebar renders both with one code path and management
 * inherits the rail, flyout panel, pinning, and active matching for free.
 *
 * Mirrors the old dashboard's management menu (`frontend/src/components/
 * sidebar.jsx`): one flat list of 46 entries divided by nine unnamed `<hr/>`s.
 * Those dividers are the only grouping it has, so this names them and turns
 * each into a group. The one exception is its third cluster, which holds 17
 * entries — unscannable as a single flyout, so it splits into Compliance /
 * Templates / Reference data.
 *
 * Only the entries with a route behind them are returned — see `onlyBuilt`.
 */
export function buildManagementNav(
  t: (key: string) => string,
  access: ManagementNavAccess,
): NavEntry[] {
  const groups: NavGroup[] = [
    {
      type: 'group',
      key: 'mgmt-accounts',
      label: t('management.accounts'),
      icon: faUsers,
      children: [
        entry('cs-tools', t('management.csTools'), 'cs-tools'),
        entry('mgmt-users', t('management.users'), 'users'),
        entry('mgmt-companies', t('management.companies'), 'companies'),
        entry(
          'company-merges',
          t('management.companyMerges'),
          'company-merges',
        ),
        entry(
          'email-bounces',
          t('management.emailBounces'),
          'email-bounce-tracking',
        ),
        entry('mgmt-permissions', t('management.permissions'), 'permissions'),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-compliance',
      label: t('management.compliance'),
      icon: faClipboardCheck,
      children: [
        entry('mgmt-documents', t('management.documents'), 'documents'),
        entry(
          'document-types',
          t('management.documentTypes'),
          'document-types',
        ),
        entry('mgmt-audits', t('management.audits'), 'audits'),
        entry('audit-types', t('management.auditTypes'), 'audit-types'),
        // The certificates themselves. Grouped with documents and audits
        // because all three are the evidence a supplier is assessed on —
        // Accounts, where the old menu kept this, is about who the users and
        // companies are. Named apart from `standards` over in Catalog, which
        // is the catalogue of what certificates are issued *against*.
        entry(
          'certifications',
          t('management.certifications'),
          'certifications',
        ),
        // The old menu also carries `audits-outdated` and
        // `audit-types-outdated` twins. Left out: it labels them outdated
        // itself, so migrating them would carry a deprecation forward.
        entry(
          'mgmt-risk-analysis',
          t('management.riskAnalysis'),
          'risk-management',
        ),
        entry(
          'slcp-dashboard',
          t('management.slcpDashboard'),
          'slcp-dashboard',
        ),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-templates',
      label: t('management.templates'),
      icon: faFileLines,
      children: [
        entry(
          'attachment-templates',
          t('management.attachmentTemplates'),
          'attachment-templates',
        ),
        entry(
          'questionnaire-templates',
          t('management.questionnaireTemplates'),
          'supplier-questionnaire-templates',
        ),
        entry(
          'custom-information-fields',
          t('management.customInformationFields'),
          'custom-information-fields',
        ),
        entry(
          'scoring-templates',
          t('management.scoringTemplates'),
          'scoring-templates',
        ),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-reference',
      label: t('management.referenceData'),
      icon: faDatabase,
      children: [
        entry(
          'production-processes',
          t('management.productionProcesses'),
          'production-processes',
        ),
        entry(
          'facility-processes',
          t('management.facilityProcesses'),
          'facility-processes',
        ),
        entry('countries', t('management.countries'), 'countries'),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-webshops',
      label: t('management.webshops'),
      icon: faStore,
      children: [
        entry('webshops', t('management.webshopsList'), 'webshops'),
        entry('webshop-origins', t('management.webshopOrigins'), 'origins'),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-platform',
      label: t('management.platform'),
      icon: faSliders,
      children: [
        entry(
          'platform-features',
          t('management.platformFeatures'),
          'platform-features',
        ),
        entry(
          'development-tags',
          t('management.developmentTags'),
          'development-tags',
        ),
        entry(
          'background-jobs',
          t('management.backgroundJobs'),
          'background-jobs',
        ),
        entry(
          'entity-history',
          t('management.entityHistory'),
          'entity-history',
        ),
        entry(
          'tag-identifications',
          t('management.tagIdentifications'),
          'tag-identifications',
        ),
        entry('translations', t('management.translations'), 'translations'),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-catalog',
      label: t('management.catalog'),
      icon: faShapes,
      children: [
        entry('materials', t('management.materials'), 'materials'),
        entry(
          'product-categories',
          t('management.productCategories'),
          'product-categories',
        ),
        // Named apart from the Compliance entry the old menu also calls
        // "Certifications": that one lists the certificates themselves, this
        // one the catalogue of standards they are issued against. One label
        // for both made them indistinguishable.
        entry('standards', t('management.standards'), 'standards'),
        entry('badges', t('management.badges'), 'badges'),
        entry(
          'product-data-points',
          t('management.productDataPoints'),
          'product-data-points',
        ),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-classification',
      label: t('management.classification'),
      icon: faCode,
      children: [
        entry('impact-areas', t('management.impactAreas'), 'impact-areas'),
      ],
    },
    {
      // Native single sign-on for customer companies: their organizations,
      // onboarding links, and the switch off Ory.
      type: 'group',
      key: 'mgmt-b2b-sso',
      label: t('management.b2bSso'),
      icon: faKey,
      children: [
        entry(
          'mgmt-sso-organizations',
          t('management.ssoOrganizations'),
          'sso',
        ),
      ],
    },
    {
      type: 'group',
      key: 'mgmt-access',
      label: t('management.accessAndFiles'),
      icon: faFolderOpen,
      children: [
        entry('mgmt-files', t('management.files'), 'files'),
        // Admin-only in the old menu, like Roles and Rights over in Accounts.
        {
          ...entry('mgmt-rules', t('management.rules'), 'rules'),
          visible: () => access.isRetracedAdmin,
        },
      ],
    },
  ];

  return onlyBuilt(
    [
      // Standalone above the groups, as the old menu has it — the tool an
      // operator opens most, and the area's landing route. Left out entirely
      // for staff who may not impersonate (see `canImpersonate`): its own
      // route guard would bounce them back out anyway, and a visible entry
      // that redirects on click reads as broken rather than restricted.
      ...(access.canImpersonate
        ? [
            {
              type: 'item' as const,
              key: 'impersonate',
              label: t('management.impersonate'),
              icon: faUserSecret,
              href: MANAGEMENT_DEFAULT_ROUTE,
              panelLabel: t('management.impersonate'),
            },
          ]
        : []),
      ...groups,
    ],
    access.hasRoute,
  );
}

/**
 * Each management page's nav label, by its URL segment (`translations` →
 * "Translations"): the breadcrumb names a management page the way the menu
 * does, in the same language. Every entry counts, whoever is looking.
 */
export function managementPageLabels(
  t: (key: string) => string,
): Map<string, string> {
  const labels = new Map<string, string>();
  const add = (href: string | undefined, label: string) => {
    const segment = href?.slice(`${MANAGEMENT_ROOT}/`.length).split('/')[0];
    if (segment && href?.startsWith(`${MANAGEMENT_ROOT}/`)) {
      labels.set(segment, label);
    }
  };
  const nav = buildManagementNav(t, {
    isRetracedAdmin: true,
    canImpersonate: true,
    hasRoute: () => true,
  });
  for (const entry of nav) {
    if (entry.type === 'item') add(entry.href, entry.label);
    if (entry.type === 'group') {
      for (const child of entry.children) add(child.href, child.label);
    }
  }
  return labels;
}
